import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import isEmail from 'validator/lib/isEmail';
import { Combobox, H5, Input, ModalWindow, RadioGroup, RadioGroupOption, Select, Spinner, Text, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { buildInvitePayload, getSignerRoleIdentity, getInternalSignerDisplayName, getInternalSignerEmail, isParticipantRole, isThirdPartyRole, participantHasSameEmailAsInternalSigner, participantHasSameEmailAsSignerEmail, } from './create_consent_modal_data';
import { useDocumentTemplates } from './use_document_templates.hook';
import { useSendConsentInvite } from './use_send_consent_invite.hook';
import { useSiteInternalSigners } from './use_site_internal_signers.hook';
const getParticipantLabel = (participant) => {
    return participant.c_email ?? participant.c_number ?? participant._id;
};
export const CreateConsentModal = ({ isOpen, participant, onClose, environment, siteId, accountId, getApiInstance, getApiErrorMessage, onInviteSuccess, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const apiClient = useMemo(() => (environment ? getApiInstance(environment) : undefined), [environment, getApiInstance]);
    // Legacy DocumentCreateParticipantRecord/connect.js: when the participant
    // has no email the site is narrowed to PTS-only and the radio is hidden.
    const isEmaillessParticipant = !participant.c_email;
    const showSignatureTypeRadio = !isEmaillessParticipant;
    const defaultSignatureType = isEmaillessParticipant ? 'pts' : 'esign';
    const [signatureType, setSignatureType] = useState(defaultSignatureType);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [selectedSiteUserIdsByRole, setSelectedSiteUserIdsByRole] = useState({});
    const [thirdPartyEmailsByRole, setThirdPartyEmailsByRole] = useState({});
    const resetState = () => {
        setSignatureType(defaultSignatureType);
        setSelectedTemplateId(null);
        setSelectedSiteUserIdsByRole({});
        setThirdPartyEmailsByRole({});
    };
    const handleClose = () => {
        resetState();
        onClose();
    };
    const { data: templates = [], isLoading: areTemplatesLoading } = useDocumentTemplates({
        apiClient,
        siteId,
    });
    const { data: internalSigners = [], isLoading: areSignersLoading } = useSiteInternalSigners({
        apiClient,
        accountId,
        siteId,
    });
    const templateDetail = useMemo(() => templates.find((template) => template._id === selectedTemplateId) ?? null, [templates, selectedTemplateId]);
    const sendInviteMutation = useSendConsentInvite({
        apiClient,
        getApiErrorMessage,
        onSuccess: () => {
            onInviteSuccess?.();
            handleClose();
        },
    });
    const templateOptions = useMemo(() => templates.map((template) => ({
        label: template.ec__title,
        description: template.ec__description,
        value: template._id,
    })), [templates]);
    const selectedTemplateOption = useMemo(() => templateOptions.find((option) => option.value === selectedTemplateId) ?? null, [templateOptions, selectedTemplateId]);
    const siteUserOptions = useMemo(() => internalSigners
        .map((signer) => ({
        label: getInternalSignerDisplayName(signer),
        value: signer._id,
        isDisabled: !getInternalSignerEmail(signer),
    }))
        .filter((option) => !!option.label), [internalSigners]);
    const nonParticipantRoles = useMemo(() => (templateDetail?.ec__signer_roles ?? []).filter((role) => !isParticipantRole(role)), [templateDetail]);
    const internalSignersByRole = useMemo(() => nonParticipantRoles.reduce((signersByRole, role) => {
        if (isThirdPartyRole(role)) {
            return signersByRole;
        }
        const roleIdentity = getSignerRoleIdentity(role);
        signersByRole[roleIdentity] = internalSigners.find((signer) => signer._id === selectedSiteUserIdsByRole[roleIdentity]);
        return signersByRole;
    }, {}), [internalSigners, nonParticipantRoles, selectedSiteUserIdsByRole]);
    const selectedSignerEmails = useMemo(() => nonParticipantRoles
        .map((role) => {
        const roleIdentity = getSignerRoleIdentity(role);
        if (isThirdPartyRole(role)) {
            return thirdPartyEmailsByRole[roleIdentity];
        }
        return getInternalSignerEmail(internalSignersByRole[roleIdentity]);
    })
        .filter((email) => !!email), [internalSignersByRole, nonParticipantRoles, thirdPartyEmailsByRole]);
    const areSignerRolesValid = useMemo(() => {
        if (!templateDetail) {
            return false;
        }
        return nonParticipantRoles.every((role) => {
            const roleIdentity = getSignerRoleIdentity(role);
            if (isThirdPartyRole(role)) {
                const email = thirdPartyEmailsByRole[roleIdentity]?.trim() ?? '';
                return role.ec__required ? isEmail(email) : !email || isEmail(email);
            }
            // Printed documents are wet-signed on paper, so no site user/CRC is
            // selected in-app — skip the internal signer requirement.
            if (signatureType === 'pts') {
                return true;
            }
            const selectedInternalSigner = internalSignersByRole[roleIdentity];
            return !!getInternalSignerEmail(selectedInternalSigner);
        });
    }, [internalSignersByRole, nonParticipantRoles, signatureType, templateDetail, thirdPartyEmailsByRole]);
    const isFormValid = !!templateDetail && areSignerRolesValid;
    const isSubmitting = sendInviteMutation.isPending;
    const showErrorToast = (subtitle) => {
        addNotification({ title: '', subtitle, type: 'error' });
    };
    const handleSubmit = () => {
        if (!templateDetail || !siteId) {
            showErrorToast(translate('error.generic'));
            return;
        }
        const hasDuplicateSignerEmail = selectedSignerEmails.some((email) => participantHasSameEmailAsSignerEmail(participant.c_email, email)) ||
            Object.values(internalSignersByRole).some((signer) => participantHasSameEmailAsInternalSigner(participant.c_email, signer));
        if (hasDuplicateSignerEmail) {
            showErrorToast(translate('error.create_consent.same_email_as_signer'));
            return;
        }
        const body = buildInvitePayload({
            template: templateDetail,
            participantId: participant._id,
            participantEmail: participant.c_email,
            internalSignersByRole,
            thirdPartyEmailsByRole,
            siteId,
            signatureType,
        });
        if (body.invites.length === 0) {
            showErrorToast(translate('error.generic'));
            return;
        }
        sendInviteMutation.mutate({ body });
    };
    const footerSecondaryActionButtons = [
        {
            label: translate('generic.cancel'),
            onClick: handleClose,
            disabled: isSubmitting,
        },
    ];
    const primaryButtonLabel = signatureType === 'pts'
        ? translate('action.create_consent.create')
        : translate('action.create_consent.send_document');
    const savingButton = (_jsx("div", { className: 'flex items-center gap-3', children: _jsx(Spinner, { size: 'md' }) }));
    return (_jsx(ModalWindow, { title: translate('label.create_consent.title'), isOpen: isOpen, closeWindow: handleClose, footerSecondaryActionButtons: footerSecondaryActionButtons, footerPrimaryActionButton: {
            disabled: !isFormValid || isSubmitting,
            label: primaryButtonLabel,
            content: isSubmitting ? savingButton : undefined,
            onClick: handleSubmit,
            id: 'create-consent-submit',
            dataTestId: 'create-consent-submit',
        }, width: 'base', dataTestId: 'create-consent-modal', children: _jsx("div", { className: 'flex flex-col gap-6 py-4', children: areTemplatesLoading ? (_jsx("div", { className: 'flex items-center justify-center py-8', children: _jsx(Spinner, {}) })) : (_jsxs(_Fragment, { children: [showSignatureTypeRadio && (_jsx("div", { children: _jsxs(RadioGroup, { label: translate('label.create_consent.signature_type'), value: signatureType, onChange: (value) => {
                                setSignatureType(value);
                                setSelectedTemplateId(null);
                                setSelectedSiteUserIdsByRole({});
                                setThirdPartyEmailsByRole({});
                            }, dataTestId: 'create-consent-signature-type', children: [_jsx(RadioGroupOption, { option: {
                                        value: 'esign',
                                        label: translate('label.create_consent.digital_document'),
                                        dataTestId: 'create-consent-signature-type-esign',
                                    } }), _jsx(RadioGroupOption, { option: {
                                        value: 'pts',
                                        label: translate('label.create_consent.printed_document'),
                                        dataTestId: 'create-consent-signature-type-pts',
                                    } })] }) })), _jsx("div", { children: _jsx(Combobox, { label: translate('label.create_consent.document_template'), placeholder: translate('label.create_consent.document_template_placeholder'), options: templateOptions, value: selectedTemplateOption, onChange: (option) => {
                                if (option && typeof option === 'object') {
                                    setSelectedTemplateId(option.value);
                                    setSelectedSiteUserIdsByRole({});
                                    setThirdPartyEmailsByRole({});
                                }
                            }, noOptionsFoundLabel: translate('info.create_consent.no_templates_found'), required: true, size: 'full', dataTestId: 'create-consent-template-select' }) }), !isEmaillessParticipant && (_jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(H5, { as: 'h3', className: 'text-sm font-medium', children: translate('label.create_consent.participant') }), _jsx(Text, { size: 'sm', className: 'text-text-secondary', children: getParticipantLabel(participant) })] })), nonParticipantRoles.map((role) => {
                        const roleIdentity = getSignerRoleIdentity(role);
                        const label = role.ec__role || translate('label.create_consent.site_user');
                        if (isThirdPartyRole(role)) {
                            return (_jsx("div", { children: _jsx(Input, { id: `create-consent-third-party-email-${roleIdentity}`, label: label, type: 'text', value: thirdPartyEmailsByRole[roleIdentity] ?? '', onChange: (event) => setThirdPartyEmailsByRole((emailsByRole) => ({
                                        ...emailsByRole,
                                        [roleIdentity]: event.target.value,
                                    })), required: role.ec__required, width: 'full', dataTestId: `create-consent-third-party-email-${roleIdentity}` }) }, roleIdentity));
                        }
                        // Printed documents are wet-signed on paper — no site user/CRC
                        // dropdown is shown.
                        if (signatureType === 'pts') {
                            return null;
                        }
                        const selectedSiteUserOption = siteUserOptions.find((option) => option.value === selectedSiteUserIdsByRole[roleIdentity]) ?? undefined;
                        return (_jsx("div", { children: _jsx(Select, { label: label, placeholder: translate('label.create_consent.site_user_placeholder'), options: siteUserOptions, value: selectedSiteUserOption, onChange: (option) => setSelectedSiteUserIdsByRole((siteUserIdsByRole) => ({
                                    ...siteUserIdsByRole,
                                    [roleIdentity]: option?.value ?? null,
                                })), disabled: areSignersLoading || siteUserOptions.length === 0, required: true, width: 'full', dataTestId: `create-consent-site-user-select-${roleIdentity}` }) }, roleIdentity));
                    })] })) }) }));
};
